import { useTranslation } from 'react-i18next';
import { localeTag } from '@/i18n/format';
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { RoutingControls, DEFAULT_ROUTING, type RoutingChoice, type TrafficControl } from './RoutingControls';
import {
  X, ChevronRight, ChevronLeft, ChevronDown, GripHorizontal,
  Play, Copy, Footprints, Car, Bike, Circle,
  CheckSquare, Square, Loader2, Minus, Maximize2,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useColumnFilters, FilterHeaderCell } from './../../components/common/ColumnFilter';
import { useColumnTranslations, useLayerTranslations } from '../../hooks/useColumnTranslations';
import { getMapColors } from '../../themes/mapTheme';

/* ══════════════════════════════════════════════════════════════
   Public types
   ══════════════════════════════════════════════════════════════ */

export interface RawFeature {
  type: string;
  id?: string;
  geometry?: { type: string; coordinates: any };
  properties: Record<string, any>;
}

export interface FeatureInfoResult {
  layerName: string;
  features: RawFeature[];
}

export interface CatchmentSelection {
  name: string;
  lat: number;
  lon: number;
  walkin: boolean;
  drive: boolean;
  motor: boolean;
  ringBuffer: boolean;
}

export interface CatchmentTimes {
  walkin: number[];
  drive: number[];
  motor: number[];
  ring: number[];
}

export type LayerControl = 'nbHood' | 'allVisible' | 'cosmos';
export type { TrafficControl, RoutingChoice, RouteDirection } from './RoutingControls';

export interface CatchmentOptions {
  layerControl: LayerControl;
  trafficControl: TrafficControl;
  /** Which way the roads are read and whether the search may cross the border (absent: the defaults). */
  routing?: RoutingChoice;
}

/* ══════════════════════════════════════════════════════════════
   Helpers
   ══════════════════════════════════════════════════════════════ */

const HIDDEN_KEYS = new Set([
  'geom', 'the_geom', 'shape', 'geometry', 'bbox',
  'gid', 'ogc_fid', 'objectid',
]);

function shouldHide(key: string): boolean {
  const k = key.toLowerCase();
  return HIDDEN_KEYS.has(k) || k.endsWith('_geom') || k.endsWith('_shape');
}

function extractPointCoord(feat: RawFeature): [number, number] | null {
  const g = feat.geometry;
  if (g?.type === 'Point' && Array.isArray(g.coordinates)) {
    return [g.coordinates[0], g.coordinates[1]];
  }
  const p = feat.properties;
  const lon = p.xCoordinate ?? p.longitude ?? p.lon ?? p.x;
  const lat = p.yCoordinate ?? p.latitude ?? p.lat ?? p.y;
  if (lon != null && lat != null) return [Number(lon), Number(lat)];
  return null;
}

function featureLabel(feat: RawFeature, idx: number): string {
  const p = feat.properties;
  return (
    p.name || p.Name || p.NAME ||
    p.Store_Name || p.store_name ||
    p.Mall_Name || p.mall_name ||
    p.adi || p.Adi ||
    p.ilce_adi || p.il_adi ||
    `Feature ${idx + 1}`
  );
}

const PAGE_SIZE = 50;

/* Legacy defaults */
const DEFAULT_WALKIN = ['5', '10', '15', '', ''];
const DEFAULT_DRIVE  = ['5', '15', '30', '45', ''];
const DEFAULT_MOTOR  = ['5', '15', '30', '45', ''];
const DEFAULT_RING   = ['0.25', '0.75', '1', '', ''];

/* ══════════════════════════════════════════════════════════════
   Drag / Resize hook
   ══════════════════════════════════════════════════════════════ */

function useDragResize(
  initialPos: { x: number; y: number },
  initialSize: { w: number; h: number },
  minSize = { w: 400, h: 300 },
) {
  const [pos, setPos] = useState(initialPos);
  const [size, setSize] = useState(initialSize);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const resizeRef = useRef<{ startX: number; startY: number; origW: number; origH: number } | null>(null);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      setPos({
        x: dragRef.current.origX + ev.clientX - dragRef.current.startX,
        y: dragRef.current.origY + ev.clientY - dragRef.current.startY,
      });
    };
    const onUp = () => { dragRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pos]);

  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    resizeRef.current = { startX: e.clientX, startY: e.clientY, origW: size.w, origH: size.h };
    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      setSize({
        w: Math.max(minSize.w, resizeRef.current.origW + ev.clientX - resizeRef.current.startX),
        h: Math.max(minSize.h, resizeRef.current.origH + ev.clientY - resizeRef.current.startY),
      });
    };
    const onUp = () => { resizeRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [size, minSize]);

  return { pos, size, onDragStart, onResizeStart };
}

/* ══════════════════════════════════════════════════════════════
   Pill button (reused for layer control & traffic control)
   ══════════════════════════════════════════════════════════════ */

const Pill: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={cn(
      'px-2 py-0.5 rounded text-[10px] font-medium transition-colors border',
      active
        ? 'bg-gn-blue-light border-gn-blue text-gn-blue'
        : 'bg-gn-secondary border-gn-light text-gn-secondary hover:text-gn-primary hover:border-gn',
    )}
  >
    {children}
  </button>
);

/* ══════════════════════════════════════════════════════════════
   Component
   ══════════════════════════════════════════════════════════════ */

interface FeatureInfoPanelProps {
  results: FeatureInfoResult[];
  onClose: () => void;
  onStartCatchment?: (
    selections: CatchmentSelection[],
    times: CatchmentTimes,
    options: CatchmentOptions,
  ) => Promise<void>;
  isLoading?: boolean;
}

export const FeatureInfoPanel: React.FC<FeatureInfoPanelProps> = ({
  results,
  onClose,
  onStartCatchment,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { pos, size, onDragStart, onResizeStart } = useDragResize(
    { x: window.innerWidth - 760, y: 12 },
    { w: Math.min(window.innerWidth * 0.9, 740), h: Math.min(window.innerHeight * 0.75, 560) },
  );

  const [minimized, setMinimized] = useState(false);
  const [activeLayerIdx, setActiveLayerIdx] = useState(0);
  const [pages, setPages] = useState<Record<number, number>>({});
  const [catchSel, setCatchSel] = useState<Record<string, { walkin: boolean; drive: boolean; motor: boolean; ring: boolean }>>({});

  /* Catchment time inputs — legacy defaults */
  const [walkinTimes, setWalkinTimes] = useState(DEFAULT_WALKIN);
  const [driveTimes, setDriveTimes]   = useState(DEFAULT_DRIVE);
  const [motorTimes, setMotorTimes]   = useState(DEFAULT_MOTOR);
  const [ringDists, setRingDists]     = useState(DEFAULT_RING);

  /* Layer control & traffic control — legacy defaults */
  const [layerControl, setLayerControl]     = useState<LayerControl>('nbHood');
  const [trafficControl, setTrafficControl] = useState<TrafficControl>('hvytrf');
  const [routing, setRouting] = useState<RoutingChoice>(DEFAULT_ROUTING);

  /* ── Column & layer translations ── */
  const { t: tCol } = useColumnTranslations();
  const { t: tLayer } = useLayerTranslations();

  const totalFeatures = results.reduce((n, r) => n + r.features.length, 0);
  const activeLayer = results[activeLayerIdx] || results[0];
  const page = pages[activeLayerIdx] || 0;

  const columns = useMemo(() => {
    if (!activeLayer) return [];
    const keySet = new Set<string>();
    activeLayer.features.forEach((f) => {
      Object.keys(f.properties).forEach((k) => { if (!shouldHide(k)) keySet.add(k); });
    });
    return Array.from(keySet);
  }, [activeLayer]);

  /* ── Column filtering ── */
  const propertiesList = useMemo(
    () => (activeLayer?.features ?? []).map((f) => f.properties),
    [activeLayer],
  );
  const { filters, setFilter, clearFilters, hasActiveFilters, filteredFeatures: filteredProps, stringColumns } =
    useColumnFilters(propertiesList, columns);

  // Map filtered properties back to { feature, originalIndex } so catchment checkboxes keep correct indices
  const filteredWithIndex = useMemo(() => {
    if (!activeLayer) return [];
    // filteredProps are the properties objects that passed filter — find their original indices
    const propSet = new Set(filteredProps);
    return activeLayer.features
      .map((f, idx) => ({ feature: f, originalIdx: idx }))
      .filter((item) => propSet.has(item.feature.properties));
  }, [activeLayer, filteredProps]);

  const totalPages = Math.ceil(filteredWithIndex.length / PAGE_SIZE);
  const pageItems = useMemo(
    () => filteredWithIndex.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filteredWithIndex, page],
  );

  // Reset page when filters change
  useEffect(() => {
    setPages((prev) => ({ ...prev, [activeLayerIdx]: 0 }));
  }, [filters, activeLayerIdx]);

  const hasCatchableFeatures = useMemo(
    () => results.some((lr) => lr.features.some((f) => extractPointCoord(f) !== null)),
    [results],
  );

  const selectedCount = useMemo(
    () => Object.values(catchSel).filter((s) => s.walkin || s.drive || s.motor || s.ring).length,
    [catchSel],
  );

  /* Which catchment types are selected across ALL features */
  const activeTypes = useMemo(() => {
    const t = { walkin: false, drive: false, motor: false, ring: false };
    Object.values(catchSel).forEach((s) => {
      if (s.walkin) t.walkin = true;
      if (s.drive) t.drive = true;
      if (s.motor) t.motor = true;
      if (s.ring) t.ring = true;
    });
    return t;
  }, [catchSel]);

  const showConfig = activeTypes.walkin || activeTypes.drive || activeTypes.motor || activeTypes.ring;

  /* ── Checkbox helpers ── */
  const toggleCatch = useCallback(
    (key: string, type: 'walkin' | 'drive' | 'motor' | 'ring') => {
      setCatchSel((prev) => {
        const cur = prev[key] || { walkin: false, drive: false, motor: false, ring: false };
        return { ...prev, [key]: { ...cur, [type]: !cur[type] } };
      });
    }, [],
  );

  const toggleAllOnPage = useCallback(
    (type: 'walkin' | 'drive' | 'motor' | 'ring') => {
      setCatchSel((prev) => {
        const next = { ...prev };
        const pts = pageItems
          .filter(({ feature }) => extractPointCoord(feature) !== null);
        const allTrue = pts.every(({ originalIdx }) => {
          const k = `${activeLayerIdx}-${originalIdx}`;
          return next[k]?.[type];
        });
        pts.forEach(({ originalIdx }) => {
          const k = `${activeLayerIdx}-${originalIdx}`;
          const cur = next[k] || { walkin: false, drive: false, motor: false, ring: false };
          next[k] = { ...cur, [type]: !allTrue };
        });
        return next;
      });
    }, [pageItems, activeLayerIdx],
  );

  /* ── Launch catchment ── */
  const handleStartCatchment = useCallback(() => {
    const selections: CatchmentSelection[] = [];
    Object.entries(catchSel).forEach(([key, sel]) => {
      if (!sel.walkin && !sel.drive && !sel.motor && !sel.ring) return;
      const [liStr, fiStr] = key.split('-');
      const feat = results[parseInt(liStr)]?.features[parseInt(fiStr)];
      if (!feat) return;
      const coord = extractPointCoord(feat);
      if (!coord) return;
      selections.push({
        name: featureLabel(feat, parseInt(fiStr)),
        lon: coord[0], lat: coord[1],
        walkin: sel.walkin, drive: sel.drive, motor: sel.motor, ringBuffer: sel.ring,
      });
    });
    if (selections.length === 0) return;

    const times: CatchmentTimes = {
      walkin: walkinTimes.map(Number),
      drive:  driveTimes.map(Number),
      motor:  motorTimes.map(Number),
      ring:   ringDists.map(Number),
    };
    const options: CatchmentOptions = { layerControl, trafficControl, routing };

    onStartCatchment?.(selections, times, options);
  }, [catchSel, results, walkinTimes, driveTimes, motorTimes, ringDists, layerControl, trafficControl, routing, onStartCatchment]);

  const setPage = (p: number) => setPages((prev) => ({ ...prev, [activeLayerIdx]: p }));

  if (results.length === 0) return null;

  /* ── Minimized: compact pill ── */
  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed z-50 flex items-center gap-2 px-3 py-2 rounded-lg bg-gn-primary border border-gn shadow-xl backdrop-blur-sm text-gn-primary hover:bg-gn-secondary transition-colors"
        style={{ left: pos.x, top: pos.y }}
      >
        <Maximize2 size={14} className="text-gn-blue" />
        <span className="text-[11px] font-medium">{t('tools.feature_info')}</span>
      </button>
    );
  }

  /* ── Tiny sub-components ── */
  const CheckIcon: React.FC<{ checked: boolean }> = ({ checked }) =>
    checked ? <CheckSquare size={13} className="text-gn-blue" /> : <Square size={13} className="text-gn-secondary" />;

  const TimeRow: React.FC<{
    icon: React.ReactNode; label: string; values: string[];
    onChange: (v: string[]) => void; unit: string;
  }> = ({ icon, label, values, onChange, unit }) => (
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="text-[10px] text-gn-primary w-10 shrink-0">{label}</span>
      {values.map((t, i) => (
        <input key={i} type="text" value={t}
          onChange={(e) => { const n = [...values]; n[i] = e.target.value; onChange(n); }}
          className="w-11 px-1 py-1 text-[11px] text-center text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
          placeholder="0" />
      ))}
      <span className="text-[9px] text-gn-secondary">{unit}</span>
    </div>
  );

  return (
    <>
      {/* ═══ Loading overlay (blocks entire screen) ═══ */}
      {isLoading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 bg-gn-primary border border-gn rounded-xl px-8 py-6 shadow-2xl">
            <Loader2 size={36} className="text-gn-blue animate-spin" />
            <div className="text-center">
              <p className="text-gn-primary font-medium text-sm">{t('analysis.started', { title: t('analysis.catchment') })}</p>
              <p className="text-gn-secondary text-[11px] mt-1">{t('analysis.analyzing')}</p>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Panel ═══ */}
      <div
        className="fixed z-50 flex flex-col rounded-lg shadow-2xl overflow-hidden"
        style={{
          left: pos.x, top: pos.y, width: size.w, height: size.h,
          backgroundColor: 'var(--gn-glass-bg)',
          border: '1px solid var(--gn-border)',
          backdropFilter: 'blur(12px)',
        }}
      >
        {/* ─── Draggable header ─── */}
        <div
          className="flex items-center justify-between px-3 py-2 shrink-0 cursor-move select-none"
          style={{ backgroundColor: 'var(--gn-glass-bg)', borderBottom: '1px solid var(--gn-border)' }}
          onMouseDown={onDragStart}
        >
          <div className="flex items-center gap-2">
            <GripHorizontal size={14} className="text-gn-secondary" />
            <div>
              <h3 className="font-semibold text-sm text-gn-primary">{t('tools.feature_info')}</h3>
              <p className="text-[10px] text-gn-secondary">
                {totalFeatures} feature{totalFeatures !== 1 ? 's' : ''} from {results.length} layer{results.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button onClick={() => setMinimized(true)} className="p-1 hover:bg-gn-tertiary rounded text-gn-secondary hover:text-gn-primary transition-colors" title={t('report.minimize')}>
            <Minus size={16} />
          </button>
          <button onClick={onClose} className="p-1 hover:bg-gn-tertiary rounded text-gn-secondary hover:text-gn-primary transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* ─── Layer selector (combobox) ─── */}
        {results.length > 1 && (
          <div className="flex items-center gap-2 px-3 py-1.5 shrink-0" style={{ borderBottom: '1px solid var(--gn-border)' }}>
            <span className="text-[10px] text-gn-secondary shrink-0">{t('style.layer')}:</span>
            <div className="relative flex-1">
              <select
                value={activeLayerIdx}
                onChange={(e) => setActiveLayerIdx(Number(e.target.value))}
                className="w-full appearance-none bg-gn-secondary border border-gn rounded px-2.5 py-1 pr-7 text-[11px] text-gn-primary focus:border-gn-blue focus:outline-none cursor-pointer"
              >
                {results.map((lr, idx) => (
                  <option key={lr.layerName} value={idx}>
                    {tLayer(lr.layerName)} ({lr.features.length})
                  </option>
                ))}
              </select>
              <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gn-secondary pointer-events-none" />
            </div>
          </div>
        )}

        {/* ─── Catchment config — auto-shows when any type selected ─── */}
        {showConfig && (
          <div className="shrink-0 px-3 py-2 space-y-2" style={{ backgroundColor: 'var(--gn-bg-tertiary)', borderBottom: '1px solid var(--gn-border)' }}>
            <span className="text-[10px] text-gn-blue font-semibold">{t('info.catchment_config')}</span>

            {/* Row 1: Layer control + Traffic control */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {/* Layer control */}
              <div className="flex items-center gap-1">
                <span className="text-[9px] text-gn-secondary mr-0.5">{t('analysis.layers')}:</span>
                <Pill active={layerControl === 'nbHood'} onClick={() => setLayerControl('nbHood')}>{t('analysis.layer_nbhood')}</Pill>
                <Pill active={layerControl === 'allVisible'} onClick={() => setLayerControl('allVisible')}>{t('analysis.layer_all')}</Pill>
                <Pill active={layerControl === 'cosmos'} onClick={() => setLayerControl('cosmos')}>{t('analysis.layer_cosmos')}</Pill>
              </div>

              {/* Traffic (drive only), direction and border */}
                <RoutingControls traffic={trafficControl} onTraffic={setTrafficControl} routing={routing} onRouting={setRouting} showTraffic={activeTypes.drive} />
            </div>

            {/* Row 2+: Time inputs for selected types only */}
            {activeTypes.walkin && (
              <TimeRow icon={<Footprints size={12} style={{ color: getMapColors().walk[0] }} />} label={t('info.walk')} values={walkinTimes} onChange={setWalkinTimes} unit={t('analysis.unit_min')} />
            )}
            {activeTypes.drive && (
              <TimeRow icon={<Car size={12} style={{ color: getMapColors().drive[0] }} />} label={t('info.drive')} values={driveTimes} onChange={setDriveTimes} unit={t('analysis.unit_min')} />
            )}
            {activeTypes.motor && (
              <TimeRow icon={<Bike size={12} style={{ color: getMapColors().motor[0] }} />} label={t('info.motor')} values={motorTimes} onChange={setMotorTimes} unit={t('analysis.unit_min')} />
            )}
            {activeTypes.ring && (
              <TimeRow icon={<Circle size={12} style={{ color: getMapColors().ringBuffer }} />} label={t('analysis.ring')} values={ringDists} onChange={setRingDists} unit={t('analysis.unit_km')} />
            )}
          </div>
        )}

        {/* ─── Data table ─── */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-[11px] border-collapse">
            <thead className="sticky top-0 z-10" style={{ backgroundColor: 'rgba(31, 41, 55, 0.95)' }}>
              <tr>
                {hasCatchableFeatures && (
                  <>
                    <th className="px-1 py-1.5 text-center cursor-pointer hover:bg-gn-tertiary" title={t('info.select_all_walk')} onClick={() => toggleAllOnPage('walkin')}>
                      <Footprints size={12} className="mx-auto" style={{ color: getMapColors().walk[0] }} />
                    </th>
                    <th className="px-1 py-1.5 text-center cursor-pointer hover:bg-gn-tertiary" title={t('info.select_all_drive')} onClick={() => toggleAllOnPage('drive')}>
                      <Car size={12} className="mx-auto" style={{ color: getMapColors().drive[0] }} />
                    </th>
                    <th className="px-1 py-1.5 text-center cursor-pointer hover:bg-gn-tertiary" title={t('info.select_all_motor')} onClick={() => toggleAllOnPage('motor')}>
                      <Bike size={12} className="mx-auto" style={{ color: getMapColors().motor[0] }} />
                    </th>
                    <th className="px-1 py-1.5 text-center cursor-pointer hover:bg-gn-tertiary" title={t('info.select_all_ring')} onClick={() => toggleAllOnPage('ring')}>
                      <Circle size={12} className="mx-auto" style={{ color: getMapColors().ringBuffer }} />
                    </th>
                  </>
                )}
                {columns.map((col) => (
                  <FilterHeaderCell
                    key={col}
                    columnName={col}
                    isFilterable={stringColumns.has(col)}
                    filterValue={filters[col] || ''}
                    onFilterChange={setFilter}
                    className="px-2 py-1.5 text-left text-gn-secondary font-semibold whitespace-nowrap"
                  >
                    {tCol(col)}
                  </FilterHeaderCell>
                ))}
                <th className="px-1 py-1.5" style={{ borderBottom: '1px solid var(--gn-border)' }} />
              </tr>
            </thead>
            <tbody>
              {pageItems.map(({ feature: feat, originalIdx }) => {
                const selKey = `${activeLayerIdx}-${originalIdx}`;
                const sel = catchSel[selKey] || { walkin: false, drive: false, motor: false, ring: false };
                const isPoint = extractPointCoord(feat) !== null;
                const isSelected = sel.walkin || sel.drive || sel.motor || sel.ring;

                return (
                  <tr key={originalIdx}
                    className={cn('transition-colors', isSelected ? 'bg-gn-blue-light' : 'hover:bg-gn-tertiary')}
                    style={{ borderBottom: '1px solid rgba(55, 65, 81, 0.25)' }}>
                    {hasCatchableFeatures && (
                      <>
                        <td className="px-1 py-1 text-center">
                          {isPoint ? <button onClick={() => toggleCatch(selKey, 'walkin')} className="hover:scale-110 transition-transform"><CheckIcon checked={sel.walkin} /></button> : <span className="text-gn-secondary">-</span>}
                        </td>
                        <td className="px-1 py-1 text-center">
                          {isPoint ? <button onClick={() => toggleCatch(selKey, 'drive')} className="hover:scale-110 transition-transform"><CheckIcon checked={sel.drive} /></button> : <span className="text-gn-secondary">-</span>}
                        </td>
                        <td className="px-1 py-1 text-center">
                          {isPoint ? <button onClick={() => toggleCatch(selKey, 'motor')} className="hover:scale-110 transition-transform"><CheckIcon checked={sel.motor} /></button> : <span className="text-gn-secondary">-</span>}
                        </td>
                        <td className="px-1 py-1 text-center">
                          {isPoint ? <button onClick={() => toggleCatch(selKey, 'ring')} className="hover:scale-110 transition-transform"><CheckIcon checked={sel.ring} /></button> : <span className="text-gn-secondary">-</span>}
                        </td>
                      </>
                    )}
                    {columns.map((col) => {
                      const val = feat.properties[col];
                      return (
                        <td key={col} className="px-2 py-1 text-gn-primary whitespace-nowrap max-w-[200px] truncate" title={val != null ? String(val) : ''}>
                          {val == null ? <span className="text-gn-muted italic">—</span>
                            : typeof val === 'number' ? val.toLocaleString(localeTag())
                            : String(val)}
                        </td>
                      );
                    })}
                    <td className="px-1 py-1">
                      <button onClick={() => navigator.clipboard.writeText(JSON.stringify(feat.properties, null, 2))}
                        className="text-gn-muted hover:text-gn-blue transition-colors" title={t('info.copy_row')}>
                        <Copy size={11} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {pageItems.length === 0 && (
                <tr>
                  <td colSpan={columns.length + (hasCatchableFeatures ? 5 : 1)} className="px-4 py-6 text-center text-gn-secondary italic">
                    {hasActiveFilters ? t('info.no_match') : t('info.none')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ─── Footer ─── */}
        <div className="flex items-center justify-between px-3 py-2 shrink-0" style={{ backgroundColor: 'var(--gn-glass-bg)', borderTop: '1px solid var(--gn-border)' }}>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
              className="p-1 rounded hover:bg-gn-tertiary disabled:opacity-30 text-gn-secondary"><ChevronLeft size={14} /></button>
            <span className="text-[10px] text-gn-secondary">{page + 1} / {totalPages || 1}</span>
            <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}
              className="p-1 rounded hover:bg-gn-tertiary disabled:opacity-30 text-gn-secondary"><ChevronRight size={14} /></button>
            <span className="text-[10px] text-gn-muted ml-1">
              {hasActiveFilters
                ? `(${filteredWithIndex.length} / ${activeLayer?.features.length || 0})`
                : `(${t('table.rows', { count: activeLayer?.features.length || 0 })})`}
            </span>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="ml-1 text-[9px] text-gn-blue hover:text-gn-blue">
                {t('table.clear_filters')}
              </button>
            )}
          </div>

          {hasCatchableFeatures && selectedCount > 0 && (
            <button
              onClick={handleStartCatchment}
              disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded transition-colors shadow-sm text-gn-primary disabled:opacity-50"
              style={{ backgroundColor: 'var(--gn-primary)' }}
              onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = 'var(--gn-primary-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--gn-primary)'; }}
            >
              <Play size={12} />
              {t('info.start_catchment', { count: selectedCount })}
            </button>
          )}
        </div>

        {/* ─── Resize handle ─── */}
        <div className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize" onMouseDown={onResizeStart} style={{ touchAction: 'none' }}>
          <svg width="16" height="16" viewBox="0 0 16 16" className="text-gn-muted">
            <path d="M14 14L8 14L14 8Z" fill="currentColor" opacity="0.4" />
            <path d="M14 14L11 14L14 11Z" fill="currentColor" opacity="0.6" />
          </svg>
        </div>
      </div>
    </>
  );
};
